import { useCallback, useMemo } from "react";

import {
    AbfLoanExpanded,
    getZcLedger,
    isStrategyDurationSame,
    LoopPositionExpanded,
    StrategyExpanded,
    useStrategies,
    useLoopsExpanded,
    LoopExpanded,
    useUnwindQuoteAndIxs,
    getLoopSlippage,
    useSwapCollateralTransaction,
    getZcCollateral,
    getZcLoanCollateral,
    BsMetaUtil,
    calculateNetApyLive,
    calculateHealthRatio,
    calculateLoanHealth,
    useOraclePrices,
    useTokenListMetadataByMints
} from "@bridgesplit/abf-react";
import {
    Column,
    Row,
    StatColumn,
    StatProps,
    TagTextAlert,
    Text,
    LabelWrapper,
    NestedCard,
    ArrowDivider,
    MEDIA
} from "@bridgesplit/ui";
import {
    StrategyDuration,
    LoopUnwindType,
    SwapCollateralTxnParams,
    DepositCollateralParams,
    TokenListMetadata
} from "@bridgesplit/abf-sdk";
import { useTransactionSender } from "app/components/transactions/util";
import { LOADING_ERROR, Result, formatDate, formatPercent, bsMath, lamportsToUiAmount } from "@bridgesplit/utils";
import { COPY } from "app/constants/copy";
import { useMediaQuery } from "@mui/material";

import { AppButton, Slippage, TokenImage, HealthChange } from "../../common";

function useRolloverData(loanExpanded: AbfLoanExpanded | undefined, loopPosition: LoopPositionExpanded | undefined) {
    const firstLedger = getZcLedger(loanExpanded);
    const firstCollateral = getZcCollateral(loanExpanded);

    const { data: strategy } = useStrategies({
        addresses: firstLedger?.strategy ? [firstLedger?.strategy] : []
    });

    const { slippagePresetsUi, defaultSlippageUi } = getLoopSlippage(loopPosition?.loopExpanded);

    const { slippagePercentDecimals } = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });

    const rolloverId = loopPosition?.loopExpanded.loopVault.rolloverId;

    const { data } = useLoopsExpanded({ loopVaults: rolloverId ? [rolloverId] : [], showHidden: true });
    const rolloverLoop = data?.find((l) => l.vaultIdentifier === rolloverId);

    const isRolloverElligible = strategyIsRolloverElligible(strategy?.[0], loopPosition, rolloverLoop);

    const { data: rolloverQuote } = useUnwindQuoteAndIxs({
        loopPositionExpanded: loopPosition,
        slippagePercentDecimals: slippagePercentDecimals ?? 0,
        unwindType: LoopUnwindType.Principal,
        rolloverLoopQuoteFetchParams: rolloverLoop?.loopVault.quoteFetchParams
    });

    const targetRedemptionMint = loopPosition?.loopExpanded.loopVault.targetRedemptionMint;

    const { getMetadata } = useTokenListMetadataByMints([targetRedemptionMint]);
    const { getOracle } = useOraclePrices([targetRedemptionMint]);

    const derivedData = useMemo(() => {
        if (!rolloverQuote) {
            return {
                redemptionOracle: undefined,
                redemptionPrice: undefined,
                redemptionMetadata: undefined,
                rolloverExpiration: undefined,
                newCollateralAmount: undefined,
                currentCollateral: undefined,
                currentCollateralPosition: undefined,
                newCollateralPosition: undefined,
                newCollateralLqt: undefined,
                currentWithdrawPosition: undefined,
                newWithdrawPosition: undefined,
                isLoading: true
            };
        }
        const redemptionOracle = getOracle(targetRedemptionMint);
        const redemptionPrice = redemptionOracle?.usdPrice ?? loopPosition?.loopExpanded.principalOracle.usdPrice;
        const redemptionMetadata = getMetadata(targetRedemptionMint) ?? loopPosition?.loanExpanded.principalMetadata;

        const rolloverExpiration = data?.find((l) => l.vaultIdentifier === rolloverId)?.loopVault.maturityDate;
        const newCollateralAmount = rolloverQuote?.minOutputAmount;

        const currentCollateral = getZcLoanCollateral(loanExpanded);

        const currentCollateralPosition = {
            amount: currentCollateral?.loanCollateral.amount,
            assetMetadata: currentCollateral?.loanCollateral.metadata,
            maturity: loopPosition?.loopExpanded.loopVault.maturityDate
        };

        const newCollateralPosition = {
            amount: newCollateralAmount,
            assetMetadata: rolloverLoop?.collateralToken,
            maturity: rolloverLoop?.loopVault.maturityDate
        };

        const newCollateralLqt = newRolloverLqt(strategy?.[0], rolloverLoop?.collateralToken.mint);

        const currentWithdrawPosition = {
            amount: calculateCollateralAmountInRedemptionMint(
                currentCollateral?.loanCollateral.amount,
                currentCollateral?.loanCollateral.usdPrice,
                redemptionPrice
            ),
            assetMetadata: redemptionMetadata,
            maturity: loopPosition?.loopExpanded.loopVault.maturityDate
        };

        const newWithdrawPosition = {
            amount: calculateCollateralAmountInRedemptionMint(newCollateralAmount, redemptionPrice, redemptionPrice),
            assetMetadata: redemptionMetadata,
            maturity: rolloverExpiration
        };

        return {
            redemptionOracle,
            redemptionPrice,
            redemptionMetadata,
            rolloverExpiration,
            newCollateralAmount,
            currentCollateral,
            currentCollateralPosition,
            newCollateralPosition,
            newCollateralLqt,
            currentWithdrawPosition,
            newWithdrawPosition
        };
    }, [
        getOracle,
        getMetadata,
        targetRedemptionMint,
        loopPosition,
        data,
        strategy,
        rolloverId,
        rolloverQuote,
        loanExpanded,
        rolloverLoop
    ]);

    return useMemo(
        () => ({
            firstLedger,
            firstCollateral,
            strategy,
            rolloverLoop,
            rolloverQuote,
            isRolloverElligible,
            slippagePercentDecimals,
            ...derivedData
        }),
        [
            firstLedger,
            firstCollateral,
            strategy,
            rolloverLoop,
            rolloverQuote,
            isRolloverElligible,
            slippagePercentDecimals,
            derivedData
        ]
    );
}

export default function RolloverPt({
    loanExpanded,
    loopPosition
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
}) {
    const send = useTransactionSender();
    const swapCollateral = useSwapCollateralTransaction();

    const {
        firstLedger,
        firstCollateral,
        strategy,
        rolloverLoop,
        rolloverQuote,
        isRolloverElligible,
        isLoading,
        currentCollateralPosition,
        newCollateralPosition,
        newCollateralLqt,
        currentWithdrawPosition,
        newWithdrawPosition,
        newCollateralAmount
    } = useRolloverData(loanExpanded, loopPosition);

    const submit = useCallback(async () => {
        if (
            !loanExpanded ||
            !firstCollateral ||
            !firstLedger ||
            !rolloverLoop ||
            !rolloverQuote ||
            newCollateralLqt === undefined
        )
            return Result.errFromMessage(LOADING_ERROR);

        const depositParams: DepositCollateralParams = {
            loan: loanExpanded.loan.address,
            depositMint: rolloverLoop.collateralToken.mint,
            decimals: rolloverLoop.collateralToken.decimals,
            amount: rolloverQuote.minOutputAmount,
            assetType: rolloverLoop.collateralToken.assetType,
            assetIdentifier: rolloverLoop.collateralToken.mint,
            expectedLoanValues: {
                expectedApy: firstLedger.apy,
                expectedLiquidationThreshold: [newCollateralLqt, 0, 0, 0, 0]
            }
        };

        const swapParams: SwapCollateralTxnParams = {
            loan: loanExpanded.loan.address,
            collateralWithdrawalIndex: firstCollateral.loanCollateral.index,
            depositCollateral: depositParams,
            cpiIxs: rolloverQuote.swapIxs,
            cpiLuts: rolloverQuote.swapLuts,
            cpiSigners: rolloverQuote.swapSigners ?? []
        };

        return await send(swapCollateral, swapParams, {
            description: "Rolling over loan"
        });
    }, [
        loanExpanded,
        firstCollateral,
        firstLedger,
        rolloverLoop,
        rolloverQuote,
        newCollateralLqt,
        send,
        swapCollateral
    ]);

    return (
        <Column spacing={2}>
            <Column spacing={1}>
                <Text variant="h4">Rollover Loop</Text>
                <Text variant="body2" color="caption">
                    {COPY.LOOP_ROLLOVER_DESCRIPTION}
                </Text>
            </Column>
            <Column spacing={2}>
                <AssetChange
                    label={"New collateral"}
                    newAsset={newCollateralPosition}
                    prevAsset={currentCollateralPosition}
                    isLoading={isLoading}
                />
                <AssetChange
                    label={"Collateral on maturity"}
                    tooltip={COPY.LOOP_ROLLOVER_WITHDRAW_TOOLTIP}
                    newAsset={newWithdrawPosition}
                    prevAsset={currentWithdrawPosition}
                    isLoading={isLoading}
                />
            </Column>
            {isRolloverElligible && (
                <Stats
                    loading={isLoading}
                    loopPosition={loopPosition}
                    rolloverLoop={rolloverLoop}
                    strategy={strategy?.[0]}
                    newCollateralAmount={newCollateralAmount}
                />
            )}

            <Column spacing={1}>
                <AppButton
                    asyncCta={{
                        onClickWithResult: submit,
                        closeDialog: "onSuccess"
                    }}
                    isTransaction
                    disabled={!isRolloverElligible || isLoading}
                >
                    Rollover
                </AppButton>
                {!isLoading && !isRolloverElligible && (
                    <TagTextAlert color="warning" icon="warning">
                        {COPY.LOOP_ROLLOVER_WARNING_MESSAGE}
                    </TagTextAlert>
                )}
            </Column>
        </Column>
    );
}

function calculateCollateralAmountInRedemptionMint(
    collateralAmount: number | undefined,
    collateralPrice: number | undefined,
    redemptionMintPrice: number | undefined
) {
    return bsMath.div(bsMath.mul(collateralAmount, collateralPrice), redemptionMintPrice);
}

function getRolloverMintStrategyDurationTerms(
    strategy: StrategyExpanded | undefined,
    rolloverLoop: LoopExpanded | undefined,
    loanExpanded: AbfLoanExpanded | undefined
) {
    const ledger = getZcLedger(loanExpanded);

    const rolloverMint = rolloverLoop?.collateralToken.mint;
    if (!strategy || !rolloverLoop || !rolloverMint || !ledger) return undefined;

    const currentDuration: StrategyDuration = {
        duration: ledger.duration,
        durationType: ledger.durationType
    };

    const rolloverMintStrategyTerm = strategy.terms[rolloverMint];
    if (!rolloverMintStrategyTerm) return undefined;

    const matchingDurationTerms = rolloverMintStrategyTerm.find(([duration]) =>
        isStrategyDurationSame(duration, currentDuration)
    );

    if (!matchingDurationTerms) return undefined;

    return rolloverMintStrategyTerm;
}

function strategyIsRolloverElligible(
    strategy: StrategyExpanded | undefined,
    loopPosition: LoopPositionExpanded | undefined,
    rolloverLoop: LoopExpanded | undefined
): boolean {
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const rolloverMint = loopPosition?.loopExpanded.loopVault.rolloverId;

    if (!strategy || !ledger || !rolloverMint || !rolloverLoop) return false;

    const currentDuration: StrategyDuration = {
        duration: ledger.duration,
        durationType: ledger.durationType
    };

    const newMintStrategyTerms = getRolloverMintStrategyDurationTerms(
        strategy,
        rolloverLoop,
        loopPosition?.loanExpanded
    );
    if (!newMintStrategyTerms) return false;

    const hasMatchingDurationTerms = isStrategyDurationSame(newMintStrategyTerms[0][0], currentDuration);
    if (!hasMatchingDurationTerms) return false;

    return true;
}

function newRolloverLqt(strategy: StrategyExpanded | undefined, newLoopMint: string | undefined): number | undefined {
    if (!strategy || !newLoopMint) return undefined;

    const newCollateral = strategy.collateral.find((c) => c.metadata.mint === newLoopMint);

    return newCollateral?.ltvInfo?.liquidationThreshold;
}

interface AssetSummary {
    amount: number | undefined;
    assetMetadata: TokenListMetadata | undefined;
    maturity: Date | undefined;
}

function AssetChange({
    label,
    tooltip,
    newAsset,
    prevAsset,
    isLoading
}: {
    label: string;
    tooltip?: string;
    newAsset: AssetSummary | undefined;
    prevAsset: AssetSummary | undefined;
    isLoading?: boolean;
}) {
    return (
        <LabelWrapper label={label} tooltip={tooltip}>
            <Row>
                <NestedCard position="left" sx={{ flexBasis: 0, flexGrow: 1, p: 1.5, pr: 3 }}>
                    <NestedAssetContent
                        metadata={prevAsset?.assetMetadata}
                        amount={prevAsset?.amount}
                        caption={
                            prevAsset?.maturity
                                ? formatDate(prevAsset.maturity, "date", { showTime: "never", alwaysShowYear: true })
                                : undefined
                        }
                        disabled
                        loading={isLoading}
                    />
                </NestedCard>
                <ArrowDivider />
                <NestedCard position="right" sx={{ flexBasis: 0, flexGrow: 1, p: 1.5, pl: 3 }}>
                    <NestedAssetContent
                        metadata={newAsset?.assetMetadata}
                        amount={lamportsToUiAmount(newAsset?.amount, newAsset?.assetMetadata?.decimals)}
                        caption={
                            newAsset?.maturity
                                ? formatDate(newAsset.maturity, "date", { showTime: "never", alwaysShowYear: true })
                                : undefined
                        }
                        loading={isLoading}
                    />
                </NestedCard>
            </Row>
        </LabelWrapper>
    );
}

function NestedAssetContent({
    metadata,
    amount,
    caption,
    disabled,
    loading
}: {
    metadata: TokenListMetadata | undefined;
    amount: number | undefined;
    caption?: string;
    disabled?: boolean;
    loading?: boolean;
}) {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const charLimit = isMobile ? 8 : 15;
    let tokenAmountText = BsMetaUtil.formatAmount(metadata, amount);
    if (tokenAmountText.length > charLimit) {
        tokenAmountText = `${tokenAmountText.slice(0, charLimit)}...`;
    }

    return (
        <Column sx={{ opacity: disabled ? 0.5 : 1 }}>
            <Row spacing={1}>
                {!loading && <TokenImage metadata={metadata} size="sm" />}
                <Text variant="h4" loading={loading}>
                    {tokenAmountText}
                </Text>
            </Row>
            {caption && (
                <Text loading={loading} variant="body2" color="caption">
                    {caption}
                </Text>
            )}
        </Column>
    );
}

function Stats({
    loopPosition,
    rolloverLoop,
    strategy,
    loading,
    newCollateralAmount
}: {
    loopPosition: LoopPositionExpanded | undefined;
    rolloverLoop: LoopExpanded | undefined;
    strategy: StrategyExpanded | undefined;
    loading?: boolean;
    newCollateralAmount: number | undefined;
}) {
    const rolloverStats = getRolloverStats(loopPosition, rolloverLoop, strategy, newCollateralAmount);

    const stats = useMemo(() => {
        const stats: StatProps[] = [
            {
                caption: "Borrow APY",
                value: [formatPercent(rolloverStats?.currentBorrowApy), formatPercent(rolloverStats?.newBorrowApy)],
                tooltip: COPY.LOOP_ROLLOVER_BORROW_APY_TOOLTIP
            },
            {
                caption: "Net APY",
                value: [formatPercent(rolloverStats?.currentLoopNetApy), formatPercent(rolloverStats?.newLoopNetApy)],
                tooltip: COPY.LOOP_ROLLOVER_NET_APY_TOOLTIP
            },
            {
                value: (
                    <HealthChange
                        variant="body2"
                        currentHealth={rolloverStats?.oldHealth}
                        previousHealth={rolloverStats?.newHealth}
                    />
                ),
                tooltip: COPY.LOOP_ROLLOVER_HEALTH_TOOLTIP,
                caption: COPY.HEALTH_FACTOR_TERM
            }
        ];

        return stats;
    }, [rolloverStats]);

    return <StatColumn loading={loading} stats={stats} variant="body2" />;
}

function getRolloverStats(
    loopPosition: LoopPositionExpanded | undefined,
    rolloverLoop: LoopExpanded | undefined,
    newStrategyTerms: StrategyExpanded | undefined,
    newCollateralAmount: number | undefined
) {
    if (!newStrategyTerms) return undefined;
    const ledger = getZcLedger(loopPosition?.loanExpanded);

    const newMintStrategyTerms = getRolloverMintStrategyDurationTerms(
        newStrategyTerms,
        rolloverLoop,
        loopPosition?.loanExpanded
    );

    const relevantMetaData = newStrategyTerms.collateral.find(
        (m) => m.metadata.mint === rolloverLoop?.collateralToken.mint
    );

    const newLiquidationThreshold = relevantMetaData?.ltvInfo?.liquidationThreshold;

    const loanExpanded = loopPosition?.loanExpanded;

    const { health: oldHealth } = calculateLoanHealth(loanExpanded);

    const totalDebt = ledger?.ledgerDebt.total;
    const totalDebtUsdValue = bsMath.mul(totalDebt, loanExpanded?.principalUsdPrice);
    const totalNewCollateralUsdValue = bsMath.mul(
        lamportsToUiAmount(newCollateralAmount, rolloverLoop?.collateralToken.decimals),
        rolloverLoop?.inputTokenOracle.usdPrice
    );
    const newBorrowApy = newMintStrategyTerms?.[0][1];

    const newHealth = calculateHealthRatio(totalDebtUsdValue, totalNewCollateralUsdValue, newLiquidationThreshold);

    const newLoopNetApy = calculateNetApyLive({
        loanCollateralMetadata: rolloverLoop?.collateralToken,
        collateralApyPct: rolloverLoop?.loopVault.collateralApyPct,
        principalApyPct: newBorrowApy,
        collateralUsdValue: totalNewCollateralUsdValue,
        totalDebt: ledger?.ledgerDebt.total,
        principalUsdPrice: rolloverLoop?.principalOracle.usdPrice,
        principalYieldPct: loopPosition?.userLoopInfo?.principalYieldPct,
        ledger
    });

    const currentBorrowApy = loopPosition?.loanExpanded?.borrowerWAvgApy;

    return {
        currentBorrowApy,
        newBorrowApy,
        currentLoopNetApy: loopPosition?.userLoopInfo?.netApyLive,
        newLoopNetApy,
        oldHealth,
        newHealth
    };
}
